import React from 'react';
import cx from 'classnames';

import { BackgroundView } from '../background';
import { WidgetsContext } from '../core/context';
import { getBoxShadow } from '../../../services/cssHelper.service';

import { INTERACTIVE_CONTENT_TYPES, PAGE_CONTAIN_STORYBOARDS } from './../../../constants/storyboardTypes';
import { resetMarginOnTablet, useInteractiveContent } from '../InteractiveContent/v2';
import styles from './section.module.scss';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import size from 'lodash/size';


const SectionWidgetComponent = ({ className, style, widgetProps, breakpoint, children, ...rest }) => {

  const context = React.useContext(WidgetsContext);
  const storyboardType = get(context, 'storyboard.type');

  const id = get(widgetProps, 'uid');
  const type = get(widgetProps, 'type');

  const stretched = get(widgetProps, 'properties.desktop.stretched', false) && !INTERACTIVE_CONTENT_TYPES.includes(storyboardType);
  const background = get(widgetProps, 'properties.desktop.background');

  const columns = size(children);
  const columnSpacing = get(widgetProps, `properties.${breakpoint}.columnSpacing`, 0);
  const adjustedColumnSpacing = columnSpacing * ((columns - 1) / columns);
  const { tabletScreens = false } = useInteractiveContent()

  var widgetStyle = {
    ...style,
    ...getStyles(widgetProps, breakpoint, storyboardType),
    ...resetMarginOnTablet(tabletScreens)
  }

  return (
    <div id={id}
      className={cx(className, styles.section, 'cs_section', {
        [styles.stretched]: stretched,
        [styles.mobile]: breakpoint === 'mobile'
      })}
      style={widgetStyle}
      data-widget-id={id}
      data-widget-type={type}
    >
      <BackgroundView background={background} breakpoint={breakpoint} />
      {React.Children.map(children,
        child => {
          return (
            React.cloneElement(child, {
              columnGap: adjustedColumnSpacing
            }, null)
          );
        })
      }
    </div>
  );
};

function getStyles(widgetProps, breakpoint, storyboardType) {
  const stretched = get(widgetProps, 'properties.desktop.stretched', false);
  const margin = get(widgetProps, 'properties.desktop.margin', 0);
  const padding = get(widgetProps, `properties.${breakpoint}.padding`, 0);
  const columnSpacing = get(widgetProps, `properties.${breakpoint}.columnSpacing`, 0);

  var widgetStyles = {
    paddingTop: `${padding}px`,
    paddingBottom: `${padding}px`
  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      width: `1200px`,
      columnGap: `${columnSpacing}px`,
      paddingLeft: `${padding}px`,
      paddingRight: `${padding}px`
    }

    if (stretched) {
        let translateY = get(widgetProps, 'properties.desktop.layout.transform.translate', '')?.split(",")?.[1] || '0px';
        widgetStyles = {
          ...widgetStyles,
          marginLeft: margin,
          width: `calc(100% - (2 * ${margin}px))`,
          transform: `translate(0px, ${translateY})`,
        }

        if(PAGE_CONTAIN_STORYBOARDS.includes(storyboardType)) {
          widgetStyles = {
            ...widgetStyles,
            width: `1200px`,
            marginLeft: `calc(50% - (1200px / 2))`,
          }
        }
    }
  }

  if (breakpoint === 'mobile') {
    let translateY = get(widgetProps, 'properties.mobile.layout.transform.translate', '')?.split(",")?.[1] || '0px';
    widgetStyles = {
      ...widgetStyles,
      width: `100%`,
      rowGap: `${columnSpacing}px`,
      transform: `translate(0px, ${translateY})`,
    }
  }

  //DropShadow
  const shadow = get(widgetProps, `properties.desktop.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  widgetStyles = {
    ...widgetStyles,
    ...shadowStyle
  }

  return widgetStyles;
}

const sectionWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint', 'children'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const SectionWidget = React.memo(SectionWidgetComponent, sectionWidgetEquals);

